import { useState, useEffect, useCallback, useRef } from 'react';
import { SECFiling, RawSECFiling } from '../types';

export const useWebSocket = (url: string) => {
  const [filings, setFilings] = useState<(SECFiling & { id: string })[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number>();

  const handleMessage = useCallback((message: { type: 'filing', data: RawSECFiling }) => {
    if (message.type === 'filing') {
      setFilings((prevFilings) => {
        const newFiling: SECFiling & { id: string } = {
          id: `${prevFilings.length}`,
          timestamp: message.data.timestamp,
          filingDate: message.data.filing_date,
          title: message.data.title,
          link: message.data.link,
          formType: message.data.form_type,
          name: message.data.name,
          cik: message.data.cik,
          ownership: message.data.ownership,
          accessionNumber: message.data.accession_number
        };
        return [newFiling, ...prevFilings];
      });
    }
  }, []);

  useEffect(() => {
    const ws = new WebSocket(url);
    wsRef.current = ws;

    // Start ping interval when connected
    const startPingInterval = () => {
      // Send ping every 30 seconds
      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
    };

    ws.onopen = () => {
      setIsConnected(true);
      setError(null);
      startPingInterval();
    };

    ws.onclose = () => {
      setIsConnected(false);
      setError('WebSocket connection closed');
      // Clear ping interval on close
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };

    ws.onerror = (event) => {
      setError('WebSocket error occurred');
      console.error('WebSocket error:', event);
    };

    ws.onmessage = (event) => {
      try {
        // Ignore pong responses
        if (event.data === 'pong') return;
        
        const message = JSON.parse(event.data);
        handleMessage(message);
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    return () => {
      ws.close();
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [url, handleMessage]);

  return { filings, isConnected, error };
};
